import React, { useEffect, useState } from 'react';
import { 
  Loader2, 
  Zap, 
  Code, 
  Smartphone, 
  Globe,
  Shield,
  Heart,
  Star,
  Award,
  Users,
  TrendingUp,
  Palette,
  Database,
  Cloud,
  Target,
  Briefcase,
  DollarSign,
  MapPin,
  Check
} from 'lucide-react';
import Lottie from 'lottie-react';
import loadingScreenApi from '../services/loadingScreenApi';

const AdminControlledLoadingScreen = ({ isLoading, onLoadingComplete }) => {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [settings, setSettings] = useState({
    title: '',
    description: '',
    loading_text: '',
    lottie_url: null,
    lottie_animation_data: null
  });
  const [cards, setCards] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [lottieData, setLottieData] = useState(null);

  const steps = [
    'Initializing application...',
    'Loading components...',
    'Preparing data...',
    'Optimizing performance...',
    'Ready to launch!'
  ];

  // Icon mapping for dynamic icons
  const iconMap = {
    Code, Smartphone, Globe, Zap, Shield, Heart, Star, Award, Users, 
    TrendingUp, Palette, Database, Cloud, Target, Briefcase, DollarSign, 
    MapPin, Check, Loader2
  };

  useEffect(() => {
    // Check for preloaded settings in localStorage first
    const preloadedSettings = localStorage.getItem('loadingScreenSettings');
    const preloadedCards = localStorage.getItem('loadingScreenCards');
    
    if (preloadedSettings) {
      try {
        const settings = JSON.parse(preloadedSettings);
        const cards = preloadedCards ? JSON.parse(preloadedCards) : [];
        
        setSettings(settings);
        setCards(cards);
        setLoadingData(false);
        
        // Clear localStorage after using the data
        localStorage.removeItem('loadingScreenSettings');
        localStorage.removeItem('loadingScreenCards');
      } catch (error) {
        console.error('Error parsing preloaded settings:', error);
        fetchLoadingScreenData();
      }
    } else {
      // Fetch data if not preloaded
      fetchLoadingScreenData();
    }
  }, []);

  const isValidLottieData = (data) => {
    // Check if the data has the required Lottie animation structure
    return data && 
           typeof data === 'object' && 
           data.v && // version
           data.fr && // frame rate
           data.w && // width
           data.h && // height
           Array.isArray(data.layers); // layers array
  };

  const fetchLottieData = async (lottieUrl) => {
    try {
      if (lottieUrl && lottieUrl.endsWith('.json')) {
        // For JSON files, fetch and parse
        const response = await fetch(lottieUrl);
        if (response.ok) {
          const data = await response.json();
          if (isValidLottieData(data)) {
            return data;
          } else {
            console.log('Invalid Lottie JSON data structure');
            return null;
          }
        }
      } else if (lottieUrl && lottieUrl.endsWith('.lottie')) {
        // For .lottie files, check content type first
        try {
          const response = await fetch(lottieUrl);
          if (response.ok) {
            const contentType = response.headers.get('content-type');
            
            if (contentType && contentType.includes('application/json')) {
              // If it's actually JSON with .lottie extension
              const data = await response.json();
              if (isValidLottieData(data)) {
                return data;
              }
            } else if (contentType && contentType.includes('application/zip')) {
              // If it's a compressed .lottie file, we can't handle it directly
              console.log('Compressed .lottie file detected - not supported by lottie-react library');
              console.log('Please use a JSON Lottie file or convert the .lottie file to JSON format');
              return null;
            } else {
              // Try to parse as text/JSON anyway
              const text = await response.text();
              try {
                const data = JSON.parse(text);
                if (isValidLottieData(data)) {
                  return data;
                }
              } catch (parseError) {
                console.log('Lottie file is not JSON format, skipping');
                return null;
              }
            }
          }
        } catch (parseError) {
          console.log('Error processing .lottie file:', parseError);
          return null;
        }
      } else if (lottieUrl) {
        // For other URLs, try to fetch as JSON
        try {
          const response = await fetch(lottieUrl);
          if (response.ok) {
            const data = await response.json();
            if (isValidLottieData(data)) {
              return data;
            } else {
              console.log('Invalid Lottie data structure');
              return null;
            }
          }
        } catch (error) {
          console.log('Could not parse as JSON, skipping');
          return null;
        }
      }
    } catch (error) {
      console.error('Error fetching Lottie data:', error);
    }
    return null;
  };

  const fetchLoadingScreenData = async () => {
    try {
      // Fetch data immediately to prevent flash of default content
      const response = await loadingScreenApi.getSettings();
      
      if (response.success && response.data.settings) {
        const settingsData = response.data.settings;
        setSettings(settingsData);
        setCards(response.data.cards || []);
        
        // Fetch Lottie data if URL is provided
        if (settingsData.lottie_url) {
          const lottieAnimationData = await fetchLottieData(settingsData.lottie_url);
          setLottieData(lottieAnimationData);
        }
        
        setLoadingData(false);
      }
    } catch (error) {
      console.error('Error fetching loading screen data:', error);
      // Continue with default settings even if fetch fails
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (isLoading) {
      // Faster progress animation
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          // Faster progress increments
          return prev + Math.random() * 15 + 5;
        });
      }, 50); // Reduced from 150ms to 50ms

      // Faster step transitions
      const stepInterval = setInterval(() => {
        setCurrentStep(prev => {
          if (prev >= steps.length - 1) {
            clearInterval(stepInterval);
            return prev;
          }
          return prev + 1;
        });
      }, 300); // Reduced from 800ms to 300ms

      return () => {
        clearInterval(stepInterval);
        clearInterval(progressInterval);
      };
    }
  }, [isLoading]);

  // Handle loading completion separately
  useEffect(() => {
    if (progress >= 100) {
      const timer = setTimeout(() => {
        onLoadingComplete();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [progress, onLoadingComplete]);

  if (!isLoading) return null;

  // Show main loading screen (no need for separate loading state)

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 z-50 flex items-center justify-center overflow-hidden">
      {/* Simplified Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-10 -left-10 w-20 h-20 bg-blue-200 dark:bg-blue-800 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-10 -right-10 w-24 h-24 bg-purple-200 dark:bg-purple-800 rounded-full opacity-20 animate-pulse"></div>
      </div>

      <div className="text-center relative z-10">
        {/* Main Logo/Brand Section */}
        <div className="mb-8">
          <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-2xl">
            <div className="w-24 h-24 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center">
              {settings.circular_image ? (
                <img 
                  src={settings.circular_image} 
                  alt={settings.title || 'Loading'} 
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : lottieData && isValidLottieData(lottieData) ? (
                <Lottie 
                  animationData={lottieData}
                  loop={true}
                  autoplay={true}
                  style={{ width: 80, height: 80 }}
                />
              ) : settings.lottie_animation_data && isValidLottieData(settings.lottie_animation_data) ? (
                <Lottie 
                  animationData={settings.lottie_animation_data}
                  loop={true}
                  autoplay={true}
                  style={{ width: 80, height: 80 }}
                />
              ) : (
                <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
              )}
            </div>
          </div>
          {settings.title && (
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              {settings.title}
            </h1>
          )}
          {settings.description && (
            <p className="text-gray-600 dark:text-gray-400 mt-3 text-lg">
              {settings.description}
            </p>
          )}
        </div>

        {/* Progress Bar */}
        <div className="w-80 mx-auto mb-8">
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 shadow-inner">
            <div 
              className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 h-3 rounded-full transition-all duration-300 ease-out shadow-lg"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mt-3">
            <span className="font-medium">{settings.loading_text || 'Loading...'}</span>
            <span className="font-bold">{Math.round(progress)}%</span>
          </div>
        </div>

        {/* Simplified Loading Dots */}
        <div className="flex justify-center space-x-2 mb-8">
          {[0, 1, 2].map((dot) => (
            <div
              key={dot}
              className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 animate-bounce shadow-lg"
              style={{
                animationDelay: `${dot * 0.2}s`,
                animationDuration: '0.8s'
              }}
            />
          ))}
        </div>

        {/* Feature Highlights with Dynamic Cards - Only show if cards exist and progress > 50% */}
        {cards.length > 0 && progress > 50 && (
          <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
            {cards.slice(0, 3).map((card, index) => {
              const IconComponent = iconMap[card.icon] || Code;
              return (
                <div key={card.id} className="text-center">
                  <div className="w-10 h-10 mx-auto mb-2 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-lg flex items-center justify-center">
                    <IconComponent className={`w-5 h-5 ${card.icon_color}`} />
                  </div>
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{card.title}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Simplified Loading Steps Indicator */}
        <div className="mt-6 flex justify-center space-x-2">
          {[0, 1, 2, 3, 4].map((index) => (
            <div key={index} className="flex items-center">
              <div className={`w-2 h-2 rounded-full ${
                index <= currentStep 
                  ? 'bg-green-500' 
                  : 'bg-gray-300 dark:bg-gray-600'
              }`} />
              {index < 4 && (
                <div className={`w-6 h-0.5 mx-1 ${
                  index < currentStep 
                    ? 'bg-green-500' 
                    : 'bg-gray-300 dark:bg-gray-600'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminControlledLoadingScreen; 